import { useCallback, useEffect, useMemo, useState } from 'react';
import gettext from '@sdk/lang/gettext';
import useModalState from '@sdk/components/ui/modal/useModalState';
import PickerPersonaItem from '@sdk/components/forms/PickerPersonaItem';

export const useMergeModal = ({
  isOpen,
  fromItems,
  toItems,
  onClose,
  onSubmit,
  placeholder = gettext('Select persona'),
  pickerInvalidStateText = gettext('* No matching names'),
}) => {
  const { isVisible, open, close } = useModalState();
  const [targetItemId, setTargetItemId] = useState(null);
  const [isConfirmation, setConfirmation] = useState(false);
  const isMultiple = fromItems?.length > 1;
  const pickerOptions = useMemo(() => {
    const fromItemIds = fromItems?.map((item) => item.id) || [];

    return toItems
      ?.filter((item) => isMultiple || !fromItemIds.includes(item.id))
      .map((item) => ({ value: item.id, ...item }));
  }, [fromItems, toItems, isMultiple]);
  const pickerProps = useMemo(
    () => ({
      options: pickerOptions,
      placeholder,
      forceOptionSelection: true,
      invalidStateText: pickerInvalidStateText,
      onChange: setTargetItemId,
      renderItem: PickerPersonaItem,
    }),
    [pickerOptions, pickerInvalidStateText, placeholder]
  );
  const onMergeClick = useCallback(() => {
    if (isConfirmation) {
      return onSubmit(fromItems, { id: targetItemId });
    }

    setConfirmation(true);

    return Promise.resolve();
  }, [fromItems, isConfirmation, onSubmit, targetItemId]);

  const onDismiss = () => {
    const step = isConfirmation ? 2 : 1;
    setConfirmation(false);
    onClose({ step });
    close();

    return Promise.resolve();
  };

  useEffect(() => {
    if (isOpen) open();
  }, [open, isOpen]);

  return {
    isConfirmation,
    isMultiple,
    isVisible,
    onDismiss,
    onMergeClick,
    pickerProps,
    targetItemId,
  };
};
